import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Cloud, Loader2 } from "lucide-react";
import { useAuth, SIGNUP_CONSENT_KEY } from "../context/AuthContext";
import ConsentModal from "../legal/ConsentModal";
import { consentPayload } from "../legal/legalConfig";
import { screenMessage } from "../lib/screenMessage";
import { inAppPath } from "../lib/inAppPath";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const AUTH_ERROR_KEY = "nimbus_auth_error";

const takeSignupConsent = () => {
  try {
    const c = JSON.parse(sessionStorage.getItem(SIGNUP_CONSENT_KEY) || "null");
    sessionStorage.removeItem(SIGNUP_CONSENT_KEY);
    return c && c.terms === true && c.privacy === true ? c : null;
  } catch {
    return null;
  }
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  // A NEW Google user who hadn't ticked the signup boxes: no account exists until they accept here.
  const [pending, setPending] = useState(null);
  const [pending2fa, setPending2fa] = useState("");
  const [code, setCode] = useState("");

  const fail = (screen) => {
    try { sessionStorage.setItem(AUTH_ERROR_KEY, screenMessage(screen)); } catch {}
    window.history.replaceState({}, document.title, "/login");
    navigate("/login", { replace: true });
  };

  const finish = async (data) => {
    const path = inAppPath(data?.next);
    window.history.replaceState({}, document.title, path);
    await refresh();
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    const consent = takeSignupConsent();

    (async () => {
      try {
        if (!sessionId) throw new Error("missing session");
        const { data } = await axios.post(`${API}/auth/session`,
          { session_id: sessionId, ...(consent ? consentPayload(consent) : {}) }, { withCredentials: true });
        if (data.staff_sign_in_required) {
          navigate(inAppPath(data.next, "/admin/login"), { replace: true });
          return;
        }
        if (data.consent_required_for_signup) {
          window.history.replaceState({}, document.title, "/workspace");
          setPending(data);
          return;
        }
        if (data.requires_2fa) {
          window.history.replaceState({}, document.title, "/login");
          setPending2fa(data.pending_2fa || "");
          return;
        }
        await finish(data);
      } catch {
        fail("login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
        <Cloud className="h-6 w-6" strokeWidth={2.2} />
      </span>
      <div className="mt-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {pending || pending2fa ? "Almost there…" : "Signing you in…"}
      </div>
      {pending2fa && (
        <form className="mt-6 w-full max-w-xs" onSubmit={async (e) => {
          e.preventDefault();
          try {
            const { data } = await axios.post(`${API}/auth/2fa`, { pending_2fa: pending2fa, code: code.trim() }, { withCredentials: true });
            setPending2fa("");
            await finish(data);
          } catch {
            fail("twofa");
          }
        }}>
          <input className="w-full rounded border px-3 py-2" value={code} onChange={(ev) => setCode(ev.target.value)}
                 autoComplete="one-time-code" placeholder="Authenticator code" data-testid="google-2fa-code" />
          <button type="submit" className="mt-3 w-full underline" data-testid="google-2fa-submit">Continue</button>
        </form>
      )}
      <ConsentModal open={!!pending} mode="oauth-signup"
        onCancel={() => { setPending(null); navigate("/login", { replace: true }); }}
        onAccept={async (c) => {
          try {
            const { data } = await axios.post(`${API}/auth/oauth/complete-signup`,
              { pending_signup_token: pending.pending_signup_token, ...consentPayload(c) }, { withCredentials: true });
            if (data.staff_sign_in_required) {
              setPending(null);
              navigate(inAppPath(data.next, "/admin/login"), { replace: true });
              return;
            }
            if (data.requires_2fa) {
              setPending(null);
              setPending2fa(data.pending_2fa || "");
              return;
            }
            setPending(null);
            await finish(data);
            return;
          } catch {
            throw new Error(screenMessage("register"));
          }
        }} />
    </div>
  );
};

export default AuthCallback;
